// POST /api/plaid/items -- the public-token exchange, per the M1 execution
// plan §5.1 (owner WS-B). The BROWSER only ever holds a `public_token`
// (single-use, dies at the bank in minutes); the resulting `access_token` is
// encrypted and stored server-side and never appears in this response or any
// other. Response accounts are typed by the frozen `accountSchema` so the
// "new accounts, highlighted" success screen renders from the same contract
// as the accounts list.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';
import { accountSchema } from './useAccounts';

export const exchangePublicTokenResponseSchema = z.object({
  item_id: z.string(),
  job_id: z.string(),
  accounts: z.array(accountSchema),
  bootstrap_status: z.enum(['queued', 'running', 'complete', 'failed']),
});

export type ExchangePublicTokenResponse = z.infer<typeof exchangePublicTokenResponseSchema>;

export const accountSetInvalidationQueryKeys = [
  ['plaid', 'items'],
  ['accounts'],
  ['transactions'],
  ['spend'],
  ['sync-runs'],
] as const;

export interface ExchangePublicTokenInput {
  public_token: string;
  reservation_id: string;
  link_session_id: string;
  institution?: { institution_id: string; name: string } | null;
}

export function useExchangePublicToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ExchangePublicTokenInput): Promise<ExchangePublicTokenResponse> => {
      const response = await client.post('/api/plaid/items', input);
      return parseEnvelope(exchangePublicTokenResponseSchema, response.data).data;
    },
    onSuccess: () => {
      for (const queryKey of accountSetInvalidationQueryKeys) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
